if (!customElements.get('advanced-countdown')) {
  class Countdown extends HTMLElement {
    constructor () {
      super();
      this._timerId = null;
      this._targetDate = null;
      this._elements = null;
    }

    connectedCallback () {
      this._init();
    }

    disconnectedCallback () {
      if (this._timerId) {
        clearInterval(this._timerId);
        this._timerId = null;
      }
    }

    _init () {
      if (this._timerId) {
        clearInterval(this._timerId);
        this._timerId = null;
      }

      const dayStr = this.dataset.day;
      const monthStr = this.dataset.month;
      const yearStr = this.dataset.year;
      const narrow = this.hasAttribute('data-narrow');
      const now = new Date();
      const currentDay = now.getDate();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();

      const rawDay = parseInt(dayStr, 10);
      const rawMonth = parseInt(monthStr, 10);
      const rawYear = parseInt(yearStr, 10);

      let month = rawMonth;
      if (Number.isNaN(month) || month <= 0 || month > 12) {
        month = currentMonth;
      }

      let year = rawYear;
      if (Number.isNaN(year) || year <= 0) {
        year = currentYear;
      }
      if (year < currentYear) {
        year = currentYear;
      }

      const useEndOfToday =
        !Number.isNaN(rawDay) &&
        rawDay === 0 &&
        month === currentMonth &&
        year === currentYear;

      let target;

      if (useEndOfToday) {
        target = new Date();
        target.setHours(23, 59, 59, 999);
      } else {
        let day = rawDay;
        if (Number.isNaN(day) || day <= 0 || day > 31) {
          day = currentDay;
        }
        const daysInMonth = new Date(year, month, 0).getDate();
        if (day > daysInMonth) {
          day = daysInMonth;
        }

        target = new Date();
        target.setFullYear(year, month - 1, day);
        target.setHours(0, 0, 0, 0);
      }

      this._targetDate = target;

      const hasPrebuiltLayout = this.querySelector('.rc-timer');
      if (hasPrebuiltLayout) {
        this._wireExistingLayout();
      } else {
        this._buildLayout(narrow);
      }

      this._updateTimer();
      this._timerId = setInterval(() => this._updateTimer(), 1000);
    }

    _wireExistingLayout () {
      const container = this.querySelector('.rc-countdown-wrapper') || this;
      const daysEl = container.querySelector('.rc-days');
      const hoursEl = container.querySelector('.rc-hours');
      const minutesEl = container.querySelector('.rc-minutes');
      const secondsEl = container.querySelector('.rc-seconds');

      let daysColonEl = null;
      if (daysEl) {
        const next = daysEl.nextElementSibling;
        if (next && next.classList.contains('rc-colon')) {
          daysColonEl = next;
        }
      }

      this._elements = {
        daysEl,
        daysColonEl,
        hoursEl,
        minutesEl,
        secondsEl,
      };
    }

    _buildLayout (narrow) {
      const container = document.createElement('span');
      container.className = 'rc-countdown-wrapper';

      const timerWrapper = document.createElement('span');
      timerWrapper.className = 'rc-timer';

      const makePart = (className) => {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = '00';
        return span;
      };

      const daysEl = makePart('rc-part rc-days');
      const hoursEl = makePart('rc-part rc-hours');
      const minutesEl = makePart('rc-part rc-minutes');
      const secondsEl = makePart('rc-part rc-seconds');

      const makeColon = () => {
        const span = document.createElement('span');
        span.className = 'rc-colon';
        span.textContent = narrow ? ':' : ' : ';
        return span;
      };

      const daysColonEl = makeColon(narrow);

      // days : hours : minutes : seconds
      timerWrapper.append(
        daysEl,
        daysColonEl,
        hoursEl,
        makeColon(narrow),
        minutesEl,
        makeColon(narrow),
        secondsEl,
      );

      container.appendChild(timerWrapper);
      this.appendChild(container);

      this._elements = {
        daysEl,
        daysColonEl,
        hoursEl,
        minutesEl,
        secondsEl,
      };
    }

    _pad2 (num) {
      return String(num).padStart(2, '0');
    }

    _getDiffComponents (from, to) {
      if (to <= from) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const start = new Date(from.getTime());
      start.setMilliseconds(0);
      const end = new Date(to.getTime());
      end.setMilliseconds(0);

      const diffMs = end - start;
      const totalSeconds = Math.floor(diffMs / 1000);

      const daySec = 24 * 60 * 60;
      const days = Math.floor(totalSeconds / daySec);

      let rest = totalSeconds - days * daySec;
      const hours = Math.floor(rest / 3600);
      rest -= hours * 3600;

      const minutes = Math.floor(rest / 60);
      const seconds = rest - minutes * 60;

      return { days, hours, minutes, seconds };
    }

    _updateTimer () {
      if (!this._targetDate || !this._elements) return;

      const now = new Date();
      const diff = this._getDiffComponents(now, this._targetDate);

      const allZero =
        diff.days <= 0 &&
        diff.hours <= 0 &&
        diff.minutes <= 0 &&
        diff.seconds <= 0;

      const { daysEl, daysColonEl, hoursEl, minutesEl, secondsEl } = this._elements;

      if (allZero) {
        if (daysEl) daysEl.textContent = '00';
        if (hoursEl) hoursEl.textContent = '00';
        if (minutesEl) minutesEl.textContent = '00';
        if (secondsEl) secondsEl.textContent = '00';

        if (this._timerId) {
          clearInterval(this._timerId);
          this._timerId = null;
        }
        return;
      }

      if (daysEl && daysColonEl) {
        if (diff.days > 0) {
          daysEl.style.display = '';
          daysColonEl.style.display = '';
          daysEl.classList.remove('hidden');
          daysColonEl.classList.remove('hidden');
          daysEl.textContent = this._pad2(diff.days);
        } else {
          daysEl.style.display = 'none';
          daysColonEl.style.display = 'none';
        }
      }

      // Hours
      if (hoursEl) {
        if (hoursEl.style.display === 'none') {
          hoursEl.style.display = '';
        }
        hoursEl.classList.remove('hidden');
        hoursEl.textContent = this._pad2(diff.hours);
      }

      // Minutes
      if (minutesEl) {
        if (minutesEl.style.display === 'none') {
          minutesEl.style.display = '';
        }
        minutesEl.classList.remove('hidden');
        minutesEl.textContent = this._pad2(diff.minutes);
      }

      // Seconds
      if (secondsEl) {
        if (secondsEl.style.display === 'none') {
          secondsEl.style.display = '';
        }
        secondsEl.classList.remove('hidden');
        secondsEl.textContent = this._pad2(diff.seconds);
      }
    }
  }

  customElements.define('advanced-countdown', Countdown);
}
